// Garmin pilot (via Stridee) — thin client for the backend's /garmin/* routes (2026-10-09).
// The browser never sees Stridee keys, ids, FIT files or activity data: only a status summary and,
// when connecting, Stridee's hosted consent URL.
import authFetch from './authFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://claude-coach-backend-1.onrender.com';

// Consent v3 (2026-10-27): v1 (receive activities via Stridee) + personal analysis by external AI services — OpenAI
// and Google Gemini (v2, 2026-10-12, named Google Gemini only). The SERVER validates the version and the explicit AI
// tick (backend garmin/consent.py) and decides AI access — and which AI provider — from the stored version only;
// nothing here can grant it. A stored v2 is offered the v3 upgrade (consent_upgrade_available).
export const GARMIN_CONSENT_VERSION = 'garmin-pilot-v3';
export const GARMIN_CONSENT_V1 = 'garmin-pilot-v1';   // data only, no AI (withdrawal target)
export const GARMIN_RETURN_PATH = '/garmin-return';   // registered (prefix) as Stridee return_uri
const STRIDEE_CONNECT_PREFIX = 'https://api.stridee.com/';

/** {eligible, status, syncing, error, completed_activities, ...} · null when the pilot is off (404). */
export async function fetchGarminStatus() {
  const res = await authFetch(`${API_BASE_URL}/garmin/status`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`garmin status ${res.status}`);
  return res.json();
}

/** Starts the link and navigates to Stridee's consent page (same tab). */
export async function startGarminConnect() {
  const res = await authFetch(`${API_BASE_URL}/garmin/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ consent: true, consent_version: GARMIN_CONSENT_VERSION, ai_consent: true }),
  });
  if (!res.ok) throw new Error(`garmin connect ${res.status}`);
  const data = await res.json();
  if (!data || typeof data.connect_url !== 'string' || !data.connect_url.startsWith(STRIDEE_CONNECT_PREFIX)) {
    throw new Error('garmin connect: unexpected link');
  }
  window.location.assign(data.connect_url);
}

/** "My Workouts": the user's completed Garmin activities (Strava-list shape, source 'garmin').
 *  [] when the pilot is off (404) or the user is not in it; throws on other failures. */
export async function fetchGarminActivities(limit = 50) {
  const res = await authFetch(`${API_BASE_URL}/garmin/activities?limit=${encodeURIComponent(limit)}`);
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`garmin activities ${res.status}`);
  const data = await res.json();
  return Array.isArray(data && data.activities) ? data.activities : [];
}

/** Per-km splits of one Garmin activity (by our row id); [] when unavailable. */
export async function fetchGarminSplits(garminId) {
  const res = await authFetch(`${API_BASE_URL}/garmin/activities/${encodeURIComponent(garminId)}/splits`);
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data && data.splits) ? data.splits : [];
}

/** The watch's own laps (structured workout steps / auto-laps). null on any failure (the panel falls
 *  back to per-km splits). `structured` = the run followed a watch workout (laps carry a step index). */
export async function fetchGarminLaps(garminId) {
  const res = await authFetch(`${API_BASE_URL}/garmin/activities/${encodeURIComponent(garminId)}/laps`);
  if (!res.ok) return null;
  const data = await res.json();
  return data && Array.isArray(data.laps) ? { structured: !!data.structured, laps: data.laps } : null;
}

/** v1 / v2 -> v3 on the EXISTING link: no disconnect, no FIT deletion, no re-import. Returns the consent view. */
export async function acceptGarminAiConsent() {
  const res = await authFetch(`${API_BASE_URL}/garmin/consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ consent: true, consent_version: GARMIN_CONSENT_VERSION, ai_consent: true }),
  });
  if (!res.ok) throw new Error(`garmin consent ${res.status}`);
  return res.json();
}

/** Withdraw the AI clause: back to v1 on the EXISTING link (sync, data and files untouched). The server stops
 *  any AI use of Garmin data as soon as the stored version is v1. Returns the consent view. */
export async function withdrawGarminAiConsent() {
  const res = await authFetch(`${API_BASE_URL}/garmin/consent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ consent: true, consent_version: GARMIN_CONSENT_V1, ai_consent: false }),
  });
  if (!res.ok) throw new Error(`garmin consent ${res.status}`);
  return res.json();
}

export async function disconnectGarmin() {
  const res = await authFetch(`${API_BASE_URL}/garmin/disconnect`, { method: 'POST' });
  if (!res.ok) throw new Error(`garmin disconnect ${res.status}`);
  return res.json();
}

/** Stridee sends the browser back to /garmin-return?status=success|denied|error&user_id=…
 *  The status is a UI HINT only (the backend trusts the signed account.connected webhook).
 *  Reads it once and cleans the URL. */
export function consumeGarminReturn() {
  if (window.location.pathname !== GARMIN_RETURN_PATH) return null;
  const status = new URLSearchParams(window.location.search).get('status');
  try { window.history.replaceState({}, '', '/'); } catch { /* ignore */ }
  return ['success', 'denied', 'error'].includes(status) ? status : 'error';
}
